// ============================================================
// DocumentsSection.tsx
// Per-case file attachments section. Composes the shared
// FileUpload and FileList components, wires them to the files
// slice in useCasevoStore.
//
// Stage 4 (approved mockup casevo-case-sections-batch3.html §1): drawn in
// the case shell's own frame (SELF_FRAMED) — section head + "Find in all
// documents", the at-a-glance strip, the full-width drop zone, the
// Document-search card rows (FileList), the optional "Elsewhere on
// <client>'s record" strip and the shared section footer.
//
// DATA LOGIC IS UNCHANGED:
//   • The store's setCurrentCaseById already kicks off a parallel
//     listFilesApi load when a case opens. DocumentsSection adds a
//     fallback fetch for the rare race where this component mounts
//     before that load fires, or where the parallel load silently
//     errored. A single handleRefresh callback is shared by both
//     upload-complete and file-deleted events so any mutation triggers
//     a fresh list — the upload-url response doesn't include the
//     uploader join, so a full re-list is the simplest correct render.
//   • Mark complete: always completable (no required files) — now the
//     shared SectionFooter (same markComplete + advanceCaseSection).
//   • The "Elsewhere" strip is READ-ONLY context: the client's other
//     documents (fetchDocumentSearchApi({ clientId }) — the same
//     access-filtered search the client page uses), minus this case's
//     files. Rows on a case the viewer can't open are shown locked.
// ============================================================
import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { useLoadPhase } from '../../hooks/useLoadPhase'
import { navigateTo } from '../../navigation/navigate'
import { listFilesApi, fetchDocumentSearchApi, requestDownloadUrlFlatApi } from '../../api/casevoApi'
import type { DocumentSearchResult } from '../../types/documentSearch.types'
import { isInlineSafeMime, openUrlInTab, triggerBlobDownload } from '../../utils/documentAccess'
import { useLiveUserName } from '../../hooks/useLiveUserName'
import FileUpload, { type PendingUpload } from '../shared/FileUpload'
import FileList, { DocBadge, DocIcon, DocGroupHead, DOC_ROW, UploaderChip, typeLine, formatBytes, formatUploader, rowDate } from '../shared/FileList'
import { CARD, BTN_SEC } from '../clientInfo/ClientInfoSections'
import { SectionHead, Glance, GlanceDash, SectionFooter } from '../case/sections/sectionKit'

interface DocumentsSectionProps {
  caseId: string
}

const ELSEWHERE_MAX = 5
// Glance "Latest" sub: "today 09:42" / "yesterday 16:42" / the row date.
const latestWhen = (iso: string, now: Date): string => {
  const d = new Date(iso)
  const hm = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  const day = (x: Date): number => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  if (day(d) === day(now)) return `today ${hm}`
  if (day(d) === new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime()) return `yesterday ${hm}`
  return rowDate(iso, now)
}

const DocumentsSection: React.FC<DocumentsSectionProps> = ({ caseId }) => {
  // Per-key selector — we only want to re-render when THIS case's
  // files slot changes, not when an unrelated slice updates.
  const files = useCasevoStore((s) => s.files[caseId])
  const setFiles = useCasevoStore((s) => s.setFiles)
  const client = useCasevoStore((s) => s.currentCase?.client ?? null)
  const authUserId = useCasevoStore((s) => s.authUser?.id ?? null)
  const liveName = useLiveUserName()

  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState<PendingUpload | null>(null)

  // Single refresh function — used by both the upload-complete and
  // file-deleted callbacks AND by FileList's "Try again" button.
  const handleRefresh = useCallback(async (): Promise<void> => {
    try {
      const result = await listFilesApi(caseId)
      setFiles(caseId, result)
      setError(null)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load files'
      setError(message)
    }
  }, [caseId, setFiles])

  // Fallback fetch on mount / caseId change. The store's parallel
  // load in setCurrentCaseById usually beats this; the early return
  // when files is already defined makes this a no-op in the common
  // case. The cancelled flag guards against React StrictMode's
  // intentional double-fire in development.
  useEffect(() => {
    if (files !== undefined) return
    let cancelled = false
    ;(async () => {
      try {
        const result = await listFilesApi(caseId)
        if (!cancelled) {
          setFiles(caseId, result)
          setError(null)
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : 'Failed to load files'
          setError(message)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [caseId, files, setFiles])

  // ── "Elsewhere on <client>'s record" (read-only context) ──
  const [elsewhere, setElsewhere] = useState<DocumentSearchResult[] | null>(null)
  const [elsewhereAll, setElsewhereAll] = useState(false)
  const [elsewhereBusy, setElsewhereBusy] = useState<string | null>(null)
  const [elsewhereError, setElsewhereError] = useState<string | null>(null)
  const clientId = client?.id ?? null
  useEffect(() => {
    if (!clientId) { setElsewhere([]); return }
    // A different case/client → back to "not loaded" so the strip can't
    // briefly show the previous case's rows.
    setElsewhere(null)
    let cancelled = false
    void fetchDocumentSearchApi({ clientId })
      .then((rows) => { if (!cancelled) setElsewhere(rows.filter((r) => r.caseId !== caseId)) })
      .catch(() => { if (!cancelled) setElsewhere([]) }) // optional context — never blocks the section
    return () => { cancelled = true }
  }, [clientId, caseId])
  const openElsewhere = async (d: DocumentSearchResult, download: boolean): Promise<void> => {
    setElsewhereError(null)
    setElsewhereBusy(d.id)
    try {
      const { downloadUrl, filename, mimeType } = await requestDownloadUrlFlatApi(d.id, !download)
      if (!download && isInlineSafeMime(mimeType)) openUrlInTab(downloadUrl)
      else await triggerBlobDownload(downloadUrl, filename)
    } catch {
      setElsewhereError(download ? 'Failed to download file. Please try again.' : 'Failed to open file. Please try again.')
    } finally {
      setElsewhereBusy(null)
    }
  }

  // Loading-vs-error: FileList renders loading first, then error.
  // If we pass loading={true} alongside a truthy error, the user
  // would never see the error bar. Flipping loading off once an
  // error is set lets FileList's error branch render (with its
  // own "Try again" button that routes back through handleRefresh
  // via the onFileDeleted prop).
  const isLoading = files === undefined && error === null
  const list = useMemo(() => files ?? [], [files])
  // ★ NO LAYOUT SHIFT (hooks/useLoadPhase): the case's files and the
  // "Elsewhere on <client>'s record" strip load in parallel and appear
  // TOGETHER — the strip no longer pops in under the list (pushing the footer
  // down) after the files have rendered. Until both are in, the list shows
  // its same-shape skeleton (invisible for the first 250ms).
  const listPhase = useLoadPhase(!isLoading && elsewhere !== null)

  // ── glance (mockup docGl) ──
  const glance = useMemo(() => {
    const now = new Date()
    const latest = list.slice().sort((a, b) => new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime())[0]
    const total = list.reduce((s, f) => s + f.sizeBytes, 0)
    const largest = list.reduce<typeof list[number] | null>((m, f) => (!m || f.sizeBytes > m.sizeBytes ? f : m), null)
    const by = new Map<string, { name: string; n: number }>()
    for (const f of list) {
      const name = f.uploadedBy === authUserId ? 'You' : (liveName(f.uploadedBy, formatUploader(f.uploader)).split(' ')[0] || 'Unknown')
      const e = by.get(f.uploadedBy) ?? { name, n: 0 }
      e.n++
      by.set(f.uploadedBy, e)
    }
    const people = Array.from(by.values()).sort((a, b) => b.n - a.n)
    const whoLatest = latest ? (latest.uploadedBy === authUserId ? 'You' : liveName(latest.uploadedBy, formatUploader(latest.uploader))) : ''
    return { now, latest, total, largest, people, whoLatest }
  }, [list, authUserId, liveName])
  const g = glance
  const glanceStrip = (
    <Glance cells={[
      { icon: 'file', label: 'Files', value: isLoading ? <GlanceDash /> : <span className="tabular-nums">{list.length}</span>, sub: 'On this case' },
      { icon: 'upload', label: 'Latest', value: g.latest ? g.latest.filename : <GlanceDash />, sub: g.latest ? `${g.whoLatest} · ${latestWhen(g.latest.uploadedAt, g.now)}` : 'Nothing uploaded yet' },
      { icon: 'layers', label: 'Storage', value: list.length ? <span className="tabular-nums">{formatBytes(g.total)}</span> : <GlanceDash />, sub: g.largest ? `Largest: ${g.largest.filename}` : 'No files' },
      { icon: 'users', label: 'Uploaded by', value: g.people.length ? `${g.people.length} ${g.people.length === 1 ? 'person' : 'people'}` : <GlanceDash />, sub: g.people.length ? g.people.map((p) => `${p.name} ${p.n}`).join(' · ') : '—' },
    ]} />
  )

  // "Find in all documents" — the firm-wide Document search (same
  // flush-then-navigate path the sidebar uses).
  const openDocumentSearch = async (): Promise<void> => {
    await navigateTo({ view: 'documentSearch' })
  }

  const clientName = client ? [client.title, client.surname].filter(Boolean).join(' ') || client.forename : ''
  const elsewhereRows = (elsewhere ?? []).slice(0, elsewhereAll ? undefined : ELSEWHERE_MAX)
  const elsewhereStrip = listPhase === 'ready' && elsewhere && elsewhere.length > 0 && (
    <div className="mt-[18px]">
      <DocGroupHead
        first
        title={`Elsewhere on ${clientName ? `${clientName}'s` : "the client's"} record`}
        sub="Client-level and other-case documents open from the client page"
        subClassName="font-medium text-casevo-text-soft"
      />
      {elsewhereError && <div role="alert" className="mb-2 text-[12.5px] text-casevo-danger">{elsewhereError}</div>}
      {elsewhereRows.map((d) => {
        const locked = d.caseAccessible === false
        const busy = elsewhereBusy === d.id
        const now = new Date()
        return (
          <div key={d.id} className={`${CARD} ${DOC_ROW} ${locked ? 'opacity-[0.62]' : ''}`}>
            <DocBadge mime={d.mimeType} filename={d.filename} muted={locked} />
            <div className="min-w-0">
              <b className={`block text-[14.5px] font-semibold truncate ${locked ? 'text-casevo-text-soft' : 'text-casevo-forest'}`}>{d.filename}</b>
              <span className="block text-[12px] text-casevo-text-soft truncate">{typeLine(d.mimeType)}</span>
              <span className="inline-flex items-center gap-1 mt-[3px] text-[11px] font-bold px-[7px] py-px rounded-[5px] bg-casevo-border-soft text-casevo-text-soft max-w-full">
                <DocIcon n={locked ? 'lock' : d.caseId ? 'brief' : 'user'} size={11} />
                <span className="truncate">
                  {d.caseId === null ? 'Client-level' : `${locked ? 'Restricted case' : 'Other case'}${d.caseRef ? ` · ${d.caseRef}` : ''}`}
                </span>
              </span>
            </div>
            <div className="hidden md:block min-w-0">{d.uploadedBy ? <UploaderChip userId={d.uploadedBy.id} fallback={d.uploadedBy.name} colour={d.uploadedBy.avatarColour} system={d.uploadedBy.system} /> : null}</div>
            <div className="hidden md:block text-[13px] text-casevo-text-soft whitespace-nowrap">{rowDate(d.uploadedAt, now)}</div>
            <div className="hidden md:block text-[13px] text-casevo-text-soft text-right tabular-nums">{formatBytes(d.sizeBytes)}</div>
            <div className="flex gap-[2px] justify-end">
              {locked ? (
                <span className="w-[30px] h-[30px] grid place-items-center text-casevo-text-soft opacity-[0.35]" role="img" title="This document is on a case you don't have access to" aria-label="This document is on a case you don't have access to">
                  <DocIcon n="lock" />
                </span>
              ) : (
                <>
                  <button type="button" disabled={busy} onClick={() => { void openElsewhere(d, false) }} title="Open" aria-label={`Open ${d.filename}`}
                    className="w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand dark:hover:text-[#a8e6c5] disabled:opacity-50 focus:outline-none casevo-focus"><DocIcon n="open" /></button>
                  <button type="button" disabled={busy} onClick={() => { void openElsewhere(d, true) }} title="Download" aria-label={`Download ${d.filename}`}
                    className="w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand dark:hover:text-[#a8e6c5] disabled:opacity-50 focus:outline-none casevo-focus"><DocIcon n="dl" /></button>
                </>
              )}
            </div>
          </div>
        )
      })}
      {!elsewhereAll && elsewhere.length > ELSEWHERE_MAX && (
        <div className="flex justify-center mt-[6px]">
          <button type="button" onClick={() => setElsewhereAll(true)}
            className="inline-flex items-center h-[30px] px-[11px] rounded-[8px] text-[12.5px] font-medium border border-casevo-border bg-casevo-surface text-casevo-forest hover:border-casevo-mid focus:outline-none casevo-focus">
            Show {elsewhere.length - ELSEWHERE_MAX} more
          </button>
        </div>
      )}
    </div>
  )

  return (
    <div className="text-[14px] leading-[1.5] text-casevo-forest">
      <SectionHead
        title="Documents"
        sub="Every file on this case. Open, download, print or delete."
        actions={
          <button type="button" className={BTN_SEC} onClick={() => { void openDocumentSearch() }}>
            <DocIcon n="search" />Find in all documents
          </button>
        }
      />
      {glanceStrip}
      <div className="mb-[14px]">
        <FileUpload
          caseId={caseId}
          onPendingChange={setPending}
          onUploadComplete={() => {
            void handleRefresh()
          }}
        />
      </div>
      <FileList
        caseId={caseId}
        files={list}
        loading={listPhase !== 'ready' && error === null}
        skeletonHidden={listPhase === 'wait'}
        error={error}
        pending={pending}
        onFileDeleted={() => {
          void handleRefresh()
        }}
      />
      {elsewhereStrip}
      {/* Sequential-flow footer — always completable (zero documents is a
          valid completion state); the shared footer runs the same
          markComplete + advanceCaseSection. */}
      <SectionFooter
        sectionKey="documents"
        errors={[]}
        readyTitle="Documents is the last step in setup"
        readySub="No required files: a case with no documents can still be completed."
      />
    </div>
  )
}

export default DocumentsSection
